/**
 * QARunRow — Expandable row showing a QA run and its individual check results.
 *
 * Click to expand and see check-by-check details in a sub-table.
 */

import { useState } from 'react';
import { ChevronRight, ExternalLink, Clock, Pencil } from 'lucide-react';
import { Spin } from 'antd';
import { cn } from '@/lib/utils';
import { TableRow, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { QACheckBadge } from './QACheckBadge';
import { useQACheckResults } from '@/hooks/useQAContentChecks';
import { getCheckLabel, getPassedCount, getWpEditUrl } from '@/types/qaContentChecks';
import type { QARun } from '@/types/qaContentChecks';

interface QARunRowProps {
  run: QARun;
  index: number;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return '--';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function QARunRow({ run, index }: QARunRowProps) {
  const [expanded, setExpanded] = useState(false);
  const { data: checks, isLoading: checksLoading } = useQACheckResults(
    expanded ? run.id : null
  );

  const wpEditUrl = getWpEditUrl(run.wp_post_url, run.wp_post_id);
  const passedCount = checks ? getPassedCount(checks) : null;
  const totalChecks = checks ? checks.length : null;

  return (
    <>
      {/* Main row */}
      <TableRow
        className={cn(
          'cursor-pointer transition-colors',
          index % 2 === 1 && 'bg-muted/20',
          expanded && 'bg-muted/40',
          'hover:bg-muted/40',
        )}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Expand chevron */}
        <TableCell className="w-8 px-2">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              expanded && 'rotate-90',
            )}
          />
        </TableCell>

        {/* Overall status */}
        <TableCell className="w-24">
          <QACheckBadge status={run.overall_result || run.status} size="md" />
        </TableCell>

        {/* Keyword (post title proxy) */}
        <TableCell>
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm truncate max-w-[300px]">
              {run.keyword}
            </span>
          </div>
        </TableCell>

        {/* Checks summary */}
        <TableCell className="w-20 text-center">
          {run.status === 'completed' && passedCount !== null && totalChecks !== null ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'text-sm font-medium tabular-nums',
                    passedCount === totalChecks ? 'text-teal-dark' : 'text-muted-foreground',
                  )}
                >
                  {passedCount}/{totalChecks}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{passedCount} of {totalChecks} checks passed</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          )}
        </TableCell>

        {/* Date */}
        <TableCell className="w-28 text-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span className="tabular-nums">{formatDate(run.started_at)}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {formatTime(run.started_at)} | Duration: {formatDuration(run.started_at, run.finished_at)}
              </p>
            </TooltipContent>
          </Tooltip>
        </TableCell>

        {/* Actions */}
        <TableCell className="w-20 text-center">
          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
            {wpEditUrl && run.overall_result === 'fail' && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={wpEditUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                </TooltipTrigger>
                <TooltipContent>Edit in WordPress</TooltipContent>
              </Tooltip>
            )}
            {run.wp_post_url && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={run.wp_post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </a>
                </TooltipTrigger>
                <TooltipContent>View post</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded check results */}
      {expanded && (
        <TableRow className="bg-muted/10 hover:bg-muted/10">
          <TableCell colSpan={6} className="p-0">
            <div className="px-8 py-3 border-t border-dashed border-border/50">
              {checksLoading ? (
                <div className="flex justify-center py-4">
                  <Spin size="small" />
                </div>
              ) : checks && checks.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground text-xs">
                      <th className="text-left py-1.5 pr-4 font-medium">Check</th>
                      <th className="text-center py-1.5 px-3 font-medium w-20">Status</th>
                      <th className="text-left py-1.5 px-3 font-medium">Actual</th>
                      <th className="text-left py-1.5 px-3 font-medium">Expected</th>
                      <th className="text-right py-1.5 pl-3 font-medium w-16">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((check) => (
                      <tr key={check.id} className="border-t border-border/30">
                        <td className="py-2 pr-4 font-medium">
                          {getCheckLabel(check.check_name)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <QACheckBadge status={check.status} size="sm" />
                        </td>
                        <td className="py-2 px-3 text-muted-foreground tabular-nums">
                          {check.actual || '--'}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground tabular-nums">
                          {check.expected || '--'}
                        </td>
                        <td className="py-2 pl-3 text-right text-muted-foreground tabular-nums text-xs">
                          {check.duration_ms !== null ? `${check.duration_ms}ms` : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-muted-foreground py-2">
                  No check results available for this run.
                </p>
              )}

              {/* Error message if run failed */}
              {run.error_message && (
                <div className="mt-2 p-2 rounded bg-destructive/5 text-destructive text-xs">
                  {run.error_message}
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
